import type { WaveRing } from "../waves/waves.js";
import { startField, type Field, type FieldOptions } from "../field/field.js";
import type { Painting } from "../waves/palette-waves.js";
import type { WaveAges } from "../waves/wave-ages.js";

// The glyph field: it wraps `#field`, the canvas it draws on, and draws around the column that
// its `carve` attribute names (an IDREF, like `for`). It starts on connect. field.ts sets it up
// for each connection, on a signal that disconnecting aborts. `data-live` means the field is
// drawing, same as on the portrait: from its first frame until its removal or a lost context.
export class JrsmGlyphField extends HTMLElement {
  private connection: AbortController | null = null;
  private field: Field | null = null;
  private readonly shown = Promise.withResolvers<Field | null>();
  /** Settles once the first connection's field draws, or with null when it can't (no WebGL2, no
      carve, a failed setup, or a removal first). */
  readonly ready: Promise<Field | null> = this.shown.promise;
  // The last palettes a wave painted, for when a reconnection starts the field again.
  private painting: Painting | null = null;

  /** The live field's wave ages (wave-ages.ts), or null without a field or once it has
      stopped. Then the rest of the page follows the fallback front. */
  ages(): WaveAges | null {
    return this.field?.ages() ?? null;
  }

  /** Turns the field to dust over its repaint (`sweep()` in field.ts). Without a live field,
      `change` just runs at once. */
  sweep(origin: { x: number; y: number }, change: () => void): Promise<void> {
    if (!this.field) {
      change();
      return Promise.resolve();
    }
    return this.field.sweep(origin, change);
  }

  /** The live waves and the palettes they paint, every frame while any wave is live. */
  setWaves(rings: readonly WaveRing[], painting: Painting): void {
    this.painting = painting;
    this.field?.setWaves(rings, painting);
  }

  connectedCallback(): void {
    void this.connect().then(this.shown.resolve);
  }

  disconnectedCallback(): void {
    this.connection?.abort();
    this.connection = null;
    this.field = null;
    this.toggleAttribute("data-live", false);
    // A lost context can't draw again, so a later connection gets a fresh canvas.
    const canvas = this.querySelector("canvas");
    canvas?.replaceWith(canvas.cloneNode(false));
  }

  // The column the field keeps clear. Its footer is where the column narrows to its end, and its
  // `.breakout` sections widen it.
  private carve(): { carve: HTMLElement; options: FieldOptions } | null {
    const carve = document.getElementById(this.getAttribute("carve") ?? "");
    if (!carve) return null;
    const footer = carve.querySelector("footer");
    return {
      carve,
      options: {
        taper: footer instanceof HTMLElement ? footer : null,
        breakouts: [...carve.querySelectorAll(".breakout")].filter(
          (element) => element instanceof HTMLElement,
        ),
      },
    };
  }

  private async connect(): Promise<Field | null> {
    const canvas = this.querySelector("canvas");
    const setup = this.carve();
    if (!canvas || !setup) {
      console.warn("Field: not drawn", new Error("jrsm-glyph-field: no canvas or carve"));
      return null;
    }
    this.connection?.abort();
    const connection = new AbortController();
    this.connection = connection;
    let started: Field | null;
    try {
      started = await startField(canvas, setup.carve, setup.options, connection.signal);
    } catch (error) {
      // A setup that fails past its own checks leaves the field empty too, the way the
      // portrait falls back to its print: its listeners end, and its context is released.
      console.warn("Field: not drawn", error);
      connection.abort();
      canvas.getContext("webgl2")?.getExtension("WEBGL_lose_context")?.loseContext();
      return null;
    }
    if (connection.signal.aborted) return started;
    this.field = started;
    if (started) {
      this.toggleAttribute("data-live", true);
      canvas.addEventListener("webglcontextlost", () => this.toggleAttribute("data-live", false), {
        once: true,
        signal: connection.signal,
      });
    }
    // A restarted field shows the palettes the last wave left, to match the rest of the page.
    if (started && this.painting) started.setWaves([], this.painting);
    return started;
  }
}

customElements.define("jrsm-glyph-field", JrsmGlyphField);
